import { useRef, useCallback, useEffect } from "react";
import { resizeableStyle } from "./style.js";

const Resizeable = ({
  children,
  resizeable,
  screenMode,
  onResize,
  style,
  initWidth,
  initHeight,
  minWidth,
  minHeight,
  onFinishResize = () => {},
}) => {
  const width = useRef(initWidth);
  const height = useRef(initHeight);
  const x = useRef(0);
  const y = useRef(0);
  const _frame = useRef();
  const resizerB = useRef();
  const resizerR = useRef();

  useEffect(() => {
    if (screenMode === 0) {
      onResize(0, 0);
      setTimeout(() => onFinishResize(0, 0), 100);
    } else if (screenMode === 1) {
      onResize(width.current, height.current);
      setTimeout(() => onFinishResize(width.current, height.current), 100);
    } else if (screenMode === 2) {
      onResize(window.innerWidth, window.innerHeight);
      setTimeout(
        () => onFinishResize(window.innerWidth, window.innerHeight),
        100
      );
    }
  }, [screenMode]);

  useEffect(() => {
    if (resizeable) {
      if (resizerB.current && resizerR.current) {
        resizerB.current.addEventListener("mousedown", resizeMouseDown);
        resizerR.current.addEventListener("mousedown", resizeMouseDown);
        resizerB.current.addEventListener("touchstart", resizeTouchStart);
        resizerR.current.addEventListener("touchstart", resizeTouchStart);
      }
    } else {
      resizerB.current.removeEventListener("mousedown", resizeMouseDown);
      resizerR.current.removeEventListener("mousedown", resizeMouseDown);
      resizerB.current.removeEventListener("touchstart", resizeTouchStart);
      resizerR.current.removeEventListener("touchstart", resizeTouchStart);
      window.removeEventListener("mousemove", resizeElement);
      window.removeEventListener("mouseup", closeResizeElement);
      window.removeEventListener("touchmove", touchResizeElement);
      window.removeEventListener("touchend", closeResizeElement);
    }
  }, [resizeable]);

  const resizeElement = useCallback((e) => {
    e = e || window.event;
    e.preventDefault();
    const dx = e.clientX - x.current;
    const dy = e.clientY - y.current;
    const newWidth = Math.max(
      minWidth,
      Math.min(width.current + dx, window.innerWidth)
    );
    const newHeight = Math.max(
      minHeight,
      Math.min(height.current + dy, window.innerWidth)
    );
    _frame.current.style.width = `${newWidth}px`;
    _frame.current.style.height = `${newHeight}px`;
    onResize(newWidth, newHeight);
  }, []);

  const touchResizeElement = useCallback((e) => {
    e = e || window.event;
    const dx = e.touches[0].clientX - x.current;
    const dy = e.touches[0].clientY - y.current;
    const newWidth = Math.max(
      minWidth,
      Math.min(width.current + dx, window.innerWidth)
    );
    const newHeight = Math.max(
      minHeight,
      Math.min(height.current + dy, window.innerWidth)
    );
    _frame.current.style.width = `${newWidth}px`;
    _frame.current.style.height = `${newHeight}px`;
    onResize(newWidth, newHeight);
  }, []);

  const closeResizeElement = useCallback((e) => {
    e = e || window.event;
    width.current = Number(_frame.current.style.width.replace(/\D./g, ""));
    height.current = Number(_frame.current.style.height.replace(/\D./g, ""));
    _frame.current.style.transition = "width 0.1s, height 0.1s";
    window.removeEventListener("mousemove", resizeElement);
    window.removeEventListener("mouseup", closeResizeElement);
    const iframes = document.getElementsByTagName("iframe");
    for (let f of iframes) {
      f.style.pointerEvents = "auto";
    }
    onFinishResize(width.current, height.current);
  }, []);

  const closeTouchResizeElement = useCallback((e) => {
    e = e || window.event;
    width.current = Number(_frame.current.style.width.replace(/\D./g, ""));
    height.current = Number(_frame.current.style.height.replace(/\D./g, ""));
    _frame.current.style.transition = "width 0.1s, height 0.1s";
    window.removeEventListener("touchmove", touchResizeElement);
    window.removeEventListener("touchend", closeTouchResizeElement);
    const iframes = document.getElementsByTagName("iframe");
    for (let f of iframes) {
      f.style.pointerEvents = "auto";
    }
    onFinishResize(width.current, height.current);
  }, []);

  const resizeMouseDown = useCallback((e) => {
    e = e || window.event;
    x.current = e.clientX;
    y.current = e.clientY;
    _frame.current.style.transition = "none";
    const dims = window.getComputedStyle(_frame.current);
    width.current = parseInt(dims.width, 10);
    height.current = parseInt(dims.height, 10);
    window.addEventListener("mousemove", resizeElement);
    window.addEventListener("mouseup", closeResizeElement);
    const iframes = document.getElementsByTagName("iframe");
    for (let f of iframes) {
      f.style.pointerEvents = "none";
    }
  }, []);

  const resizeTouchStart = useCallback((e) => {
    e = e || window.event;
    x.current = e.touches[0].clientX;
    y.current = e.touches[0].clientY;
    _frame.current.style.transition = "none";
    const dims = window.getComputedStyle(_frame.current);
    width.current = parseInt(dims.width, 10);
    height.current = parseInt(dims.height, 10);
    window.addEventListener("touchmove", touchResizeElement);
    window.addEventListener("touchend", closeTouchResizeElement);
    const iframes = document.getElementsByTagName("iframe");
    for (let f of iframes) {
      f.style.pointerEvents = "none";
    }
  }, []);

  return (
    <div
      ref={_frame}
      style={{
        ...resizeableStyle.frame,
        width:
          screenMode === 0
            ? 120
            : screenMode === 2
            ? window.innerWidth
            : width.current,
        height:
          screenMode === 0
            ? 0
            : screenMode === 2
            ? window.innerHeight
            : height.current,
        transition: "width 0.1s, height 0.1s",
        ...style,
      }}
    >
      {children}
      <div ref={resizerB} style={resizeableStyle.resizerB}></div>
      <div ref={resizerR} style={resizeableStyle.resizerR}></div>
    </div>
  );
};

export default Resizeable;
